declare global {
    interface DocumentEventMap {
        'open_member_area_modal': CustomEvent<{ $modal: MemberAreaModalHTMLElement }>;
        'close_member_area_modal': CustomEvent<{ $modal: MemberAreaModalHTMLElement }>;
    }
}

export interface MemberAreaModalHTMLElement extends HTMLElement {
    open(): void;
    close(): void;
}

type MemberAreaModalType = {
    $modalsOpened: MemberAreaModalHTMLElement[],
    init(): void,
    onKeyDown(e: KeyboardEvent): void,
    openModal($modal: MemberAreaModalHTMLElement): void,
    closeModal($modal?: MemberAreaModalHTMLElement): void,
};

const MemberAreaModal: MemberAreaModalType = {
    $modalsOpened: [],

    init() : void {
        this.$modalsOpened = [];
        const that = this;

        document.querySelectorAll<MemberAreaModalHTMLElement>('.member-area-modal').forEach($modal => {
            $modal.open = () => this.openModal($modal);
            $modal.close = () => this.closeModal($modal);
        });

        const $autoOpenModal = document.querySelector<MemberAreaModalHTMLElement>('.member-area-modal.auto-open');

        if ($autoOpenModal) {
            this.openModal($autoOpenModal);
        }

        document.addEventListener('click', e => {
            const $targetEl = e.target as HTMLElement;

            if ($targetEl.classList.contains('js-member-area-modal-toggler') || $targetEl.closest('.js-member-area-modal-toggler')) { // Toggler
                e.preventDefault();

                const $target = ($targetEl.classList.contains('js-member-area-modal-target') ? $targetEl : $targetEl.closest('.js-member-area-modal-target')) as HTMLElement;
                
                const modalId = $target.dataset.memberAreaModalTarget as string;
                const $modal = document.getElementById(modalId) as MemberAreaModalHTMLElement;

                if ($modal) {
                    if ($modal.classList.contains('open')) {
                        that.closeModal($modal);
                    } else {
                        that.openModal($modal);
                    }
                }
            } else if ($targetEl.classList.contains('js-member-area-modal-close') || $targetEl.closest('.js-member-area-modal-close')) { // Close button
                e.preventDefault();

                const $target = ($targetEl.classList.contains('js-member-area-modal-close') ? $targetEl : $targetEl.closest('.js-member-area-modal-close')) as HTMLElement;
                
                const modalId = $target.dataset.memberAreaModalTarget as string;
                const $modal = document.getElementById(modalId) as MemberAreaModalHTMLElement;

                if ($modal) {
                    that.closeModal($modal);
                }
            } else if ($targetEl.classList.contains('js-member-area-modal-open') || $targetEl.closest('.js-member-area-modal-open')) { // Open button
                e.preventDefault();

                const $target = ($targetEl.classList.contains('js-member-area-modal-open') ? $targetEl : $targetEl.closest('.js-member-area-modal-open')) as HTMLElement;
                
                const modalId = $target.dataset.memberAreaModalTarget as string;
                const $modal = document.getElementById(modalId) as MemberAreaModalHTMLElement;

                if ($modal) {
                    that.openModal($modal);
                }
            } else if ($targetEl.classList.contains('member-area-modal') && $targetEl.classList.contains('open')) { // Overlay
                that.closeModal($targetEl as MemberAreaModalHTMLElement);
            }
        });

        document.addEventListener('open_member_area_modal', e => {
            that.openModal(e.detail.$modal);
        });

        document.addEventListener('close_member_area_modal', e => {
            that.closeModal(e.detail.$modal);
        });
    },

    /**
     * Close modal when pressing Escape key
     * 
     * @param {KeyboardEvent} e 
     * 
     * @return {void}
     */
    onKeyDown(e: KeyboardEvent) : void {
        if (e.key === 'Escape') {
            MemberAreaModal.closeModal();
        }
    },

    /**
     * Open a modal
     * 
     * @param {MemberAreaModalHTMLElement} $modal 
     * 
     * @return {void}
     */
    openModal($modal: MemberAreaModalHTMLElement) : void {
        if (this.$modalsOpened.length === 0) {
            document.addEventListener('keydown', this.onKeyDown);

            document.body.classList.add('no-scroll');
        }

        $modal.style.zIndex = '999' + this.$modalsOpened.length;
        $modal.classList.add('open');

        this.$modalsOpened.push($modal);
    },

    /**
     * Close last opened modal
     * 
     * @param {MemberAreaModalHTMLElement|null} $modal 
     * 
     * @return {void}
     */
    closeModal($modal?: MemberAreaModalHTMLElement) : void {
        if (this.$modalsOpened.length === 1) {
            document.removeEventListener('keydown', this.onKeyDown);
            document.body.classList.remove('no-scroll');
        }

        const $modalToClose = $modal || this.$modalsOpened[this.$modalsOpened.length - 1];

        $modalToClose.classList.remove('open');
        $modalToClose.style.zIndex = '';

        this.$modalsOpened = this.$modalsOpened.filter($m => $m !== $modalToClose);
    },
};

export default MemberAreaModal;
